import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Cookies from 'universal-cookie';
import cn from 'classnames';
import * as PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import iconEye from '@iconify-icons/mdi/eye-outline';
import iconEyeOff from '@iconify-icons/mdi/eye-off-outline';
import iconCheck from '@iconify-icons/mdi/check';
import iconEmail from '@iconify-icons/mdi/email-outline';
/**/
import { CheckoutCard } from '../../common/CheckoutCard';
import { Input } from '../../Input';
import InputPhone from '../../InputPhone';

import { useAuth0 } from '../../../context/auth0';
import auth0 from 'auth0-js';
import {
  logBrazeAccountRegistration,
  setBrazeAuthMethod,
  setBrazeEmailSubscribe,
  logBrazeNewsletterSubscribed,
} from '../../../command/braze';

const Login = (props) => {
  const { loginWithRedirect } = useAuth0();

  const { register, getValues, errors, setValue, trigger } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  // Shared initialization code
  const cookies = new Cookies();
  const affiliate_cookie = cookies.get('affiliate_data') ?? null;
  const cds_user = affiliate_cookie && affiliate_cookie.utm_campaign === 'cds' ? affiliate_cookie : null;
  const welfare_user = cookies.get('welfare_user') ?? null;
  let provided_user = null;

  if (welfare_user) {
    provided_user = {
      first_name: welfare_user.firstName,
      last_name: welfare_user.lastName,
      email: welfare_user.email,
    };
  } else if (cds_user) {
    provided_user = {
      first_name: cds_user.name,
      last_name: cds_user.surname,
      email: cds_user.email,
    };
  }

  const webAuth = new auth0.WebAuth({
    domain: process.env.REACT_APP_AUTH0_DOMAIN,
    clientID: process.env.REACT_APP_AUTH0_CLIENT_ID,
    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    scope: 'openid profile email',
    redirectUri: window.location.origin,
    responseType: 'token',
  });

  function auth0Google() {
    cookies.set('target_url', window.location.pathname, { path: '/' });
    webAuth.authorize({
      connection: 'google-oauth2',
    });
  }

  function auth0Facebook() {
    cookies.set('target_url', window.location.pathname, { path: '/' });
    webAuth.authorize({
      connection: 'facebook',
    });
  }

  const auth0Apple = () => {
    cookies.set('target_url', window.location.pathname, { path: '/' });
    webAuth.authorize({
      connection: 'apple',
    });
  };

  function auth0Default() {
    cookies.set('target_url', window.location.pathname, { path: '/' });
    webAuth.authorize({
      connection: 'DB-wpdbfreedomeit',
    });
  }

  const signupInFlightRef = useRef(false);

  function auth0Signup() {
    if (signupInFlightRef.current) return;
    signupInFlightRef.current = true;

    webAuth.signupAndAuthorize(
      {
        connection: 'DB-wpdbfreedomeit',
        email: getValues('billing_email'),
        password: getValues('password'),
        user_metadata: {
          first_name: getValues('billing_first_name'),
          last_name: getValues('billing_last_name'),
          phone: getValues('billing_phone'),
        },
      },
      function (err, authResult) {
        signupInFlightRef.current = false;
        if (err) {
          // return console.log(err);
        }
        cookies.set('auth0_token', authResult.accessToken, { path: '/' });

        // Braze: log account registration during checkout
        logBrazeAccountRegistration('email_password', 'checkout');
        setBrazeAuthMethod('email_password');

        // Record newsletter choice now so it survives a failed order.
        // checkout.js reads window.__newsletterTrackedThisCheckout to skip its duplicate Braze block.
        const newsletterChecked = document.querySelector('#freedome-consent-marketing');
        const isNewsletterChecked = !!(newsletterChecked && newsletterChecked.checked);
        if (isNewsletterChecked) {
          logBrazeNewsletterSubscribed('checkout');
        } else {
          setBrazeEmailSubscribe(false);
        }
        window.__newsletterTrackedThisCheckout = true;
      }
    );
  }

  useEffect(() => {
    const handleCheckoutSubmitStart = () => {
      if (getValues('billing_email') && !cookies.get('auth0_token')) {
        auth0Signup();
      }
    };

    document.addEventListener('checkout_submit_start', handleCheckoutSubmitStart);
    return () => {
      document.removeEventListener('checkout_submit_start', handleCheckoutSubmitStart);
    };
  }, []);

  const errorMessages = {
    billing_first_name: app_main_wpml.first_name_required,
    billing_last_name: app_main_wpml.last_name_required,
    billing_email: app_main_wpml.email_invalid,
    password: app_main_wpml.password_invalid,
    billing_phone: app_main_wpml.phone_invalid,
  };

  const ErrorMessage = ({ name }) => {
    if (!errors[name]) return null;
    return (
      <span style={{ color: '#CC1D1D', fontSize: '12px', marginTop: '-0.3rem', marginBottom: '0.75rem', display: 'block', paddingLeft: '21px' }}>
        {errorMessages[name]}
      </span>
    );
  };

  const CrossIcon = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0.198868 0.19897C0.464026 -0.0661865 0.893932 -0.0661864 1.15909 0.19897L9.80108 8.84092C10.0662 9.10607 10.0662 9.53598 9.80108 9.80113C9.53592 10.0663 9.10602 10.0663 8.84086 9.80113L0.198868 1.15919C-0.0662894 0.89403 -0.0662895 0.464126 0.198868 0.19897Z" fill="#CC1D1D" />
      <path d="M9.80113 0.198867C10.0663 0.464024 10.0663 0.893927 9.80113 1.15908L1.15914 9.80103C0.893983 10.0662 0.464078 10.0662 0.19892 9.80103C-0.0662381 9.53587 -0.0662379 9.10597 0.19892 8.84081L8.84091 0.198867C9.10607 -0.0662891 9.53597 -0.0662891 9.80113 0.198867Z" fill="#CC1D1D" />
    </svg>
  );

  const CheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M11.0193 2.48101C11.167 2.62883 11.25 2.82928 11.25 3.03829C11.25 3.24731 11.167 3.44776 11.0193 3.59557L5.11353 9.50364C5.03549 9.58173 4.94283 9.64368 4.84085 9.68595C4.73886 9.72821 4.62956 9.74997 4.51917 9.74997C4.40879 9.74997 4.29948 9.72821 4.1975 9.68595C4.09552 9.64368 4.00286 9.58173 3.92481 9.50364L0.990563 6.56879C0.915308 6.49608 0.855282 6.4091 0.813987 6.31293C0.772692 6.21676 0.750956 6.11333 0.750047 6.00867C0.749138 5.90401 0.769074 5.80021 0.808692 5.70334C0.84831 5.60647 0.906816 5.51847 0.980797 5.44446C1.05478 5.37045 1.14275 5.31192 1.23958 5.27229C1.33642 5.23265 1.44017 5.21271 1.54479 5.21362C1.64941 5.21453 1.75281 5.23627 1.84894 5.27758C1.94507 5.31889 2.03201 5.37894 2.10469 5.45423L4.51891 7.86937L9.90467 2.48101C9.97784 2.40777 10.0647 2.34966 10.1604 2.31002C10.256 2.27037 10.3585 2.24997 10.462 2.24997C10.5655 2.24997 10.668 2.27037 10.7636 2.31002C10.8593 2.34966 10.9462 2.40777 11.0193 2.48101Z" fill="#00883F" />
    </svg>

  )
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);

  const isPhoneValid = (value) => value && value.replace(/\s/g, '').length >= 10;
  const phoneErrored = phoneTouched && !isPhoneValid(phoneValue);

  const validatePassword = (pwd) => {
    return {
      minLength: pwd.length >= 8,
      hasUpperAndNumber: /[A-Z]/.test(pwd) && /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;\/`~]/.test(pwd),
    };
  };

  const passwordValidation = validatePassword(password);

  return (
    <>
      <p className={cn('highlight__small', 'light-grey', 'text-left')} style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>
        <strong>{app_main_wpml.already_registered}</strong>{' '}
        <span onClick={loginWithRedirect} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
          {app_main_wpml.login}
        </span>
      </p>
      <CheckoutCard
        title={app_main_wpml.insert_data}
        showTitleOnMbl={true}
        prefixesAreVisible={props.prefixesAreVisible}
      >
        <div className={'login'}>
          {!provided_user
            ? [
              <span onClick={auth0Google} className={cn('btn', 'login__btn')} style={{ fontFamily: 'Bricolage Grotesque, sans-serif', position: 'relative' }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)' }}>
                  <rect width="20" height="20" fill="white" fill-opacity="0.01" />
                  <path d="M19.375 10.2084C19.375 9.43761 19.3112 8.87511 19.1731 8.29178H10.1913V11.7709H15.4634C15.3571 12.6355 14.7832 13.9376 13.5076 14.8126L13.4898 14.9291L16.3296 17.0851L16.5264 17.1043C18.3333 15.4689 19.375 13.0626 19.375 10.2084Z" fill="#4285F4" />
                  <path d="M10.1913 19.375C12.7742 19.375 14.9425 18.5418 16.5264 17.1043L13.5076 14.8126C12.6998 15.3647 11.6156 15.7499 10.1913 15.7499C7.66158 15.7499 5.51449 14.1145 4.74911 11.8541L4.63692 11.8634L1.684 14.103L1.64538 14.2082C3.2185 17.2707 6.4498 19.375 10.1913 19.375Z" fill="#34A853" />
                  <path d="M4.74911 11.8541C4.54716 11.2708 4.43028 10.6458 4.43028 10C4.43028 9.35416 4.54716 8.72918 4.73848 8.14586L4.73314 8.02162L1.74321 5.74605L1.64538 5.79165C0.997028 7.0625 0.625 8.48962 0.625 10C0.625 11.5105 0.997028 12.9374 1.64538 14.2082L4.74911 11.8541Z" fill="#FBBC05" />
                  <path d="M10.1913 4.24999C11.9876 4.24999 13.1994 5.01041 13.8903 5.64587L16.5901 3.06251C14.932 1.55209 12.7742 0.625 10.1913 0.625C6.4498 0.625 3.2185 2.72916 1.64538 5.79165L4.73848 8.14586C5.51449 5.88543 7.66158 4.24999 10.1913 4.24999Z" fill="#EB4335" />
                </svg>
                {app_main_wpml.continue_with_google}
              </span>,
              <span onClick={auth0Facebook} className={cn('btn', 'login__btn')} style={{ fontFamily: 'Bricolage Grotesque, sans-serif', position: 'relative' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#0866ff" style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)' }}>
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                {app_main_wpml.continue_with_facebook}
              </span>,
              <span onClick={auth0Apple} className={cn('btn', 'login__btn')} style={{ fontFamily: 'Bricolage Grotesque, sans-serif', position: 'relative' }}>
                <svg fill="black" height="20" width="20" viewBox="0 0 22.773 22.773" style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)' }}>
                  <path d="M15.769,0c0.053,0,0.106,0,0.162,0c0.13,1.606-0.483,2.806-1.228,3.675c-0.731,0.863-1.732,1.7-3.351,1.573 c-0.108-1.583,0.506-2.694,1.25-3.561C13.292,0.879,14.557,0.16,15.769,0z"></path>
                  <path d="M20.67,16.716c0,0.016,0,0.03,0,0.045c-0.455,1.378-1.104,2.559-1.896,3.655c-0.723,0.995-1.609,2.334-3.191,2.334 c-1.367,0-2.275-0.879-3.676-0.903c-1.482-0.024-2.297,0.735-3.652,0.926c-0.155,0-0.31,0-0.462,0 c-0.995-0.144-1.798-0.932-2.383-1.642c-1.725-2.098-3.058-4.808-3.306-8.276c0-0.34,0-0.679,0-1.019 c0.105-2.482,1.311-4.5,2.914-5.478c0.846-0.52,2.009-0.963,3.304-0.765c0.555,0.086,1.122,0.276,1.619,0.464 c0.471,0.181,1.06,0.502,1.618,0.485c0.378-0.011,0.754-0.208,1.135-0.347c1.116-0.403,2.21-0.865,3.652-0.648 c1.733,0.262,2.963,1.032,3.723,2.22c-1.466,0.933-2.625,2.339-2.427,4.74C17.818,14.688,19.086,15.964,20.67,16.716z"></path>
                </svg>
                {app_main_wpml.continue_with_apple}
              </span>,
              <p className={'text-center'} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ flex: 1, height: '1px', backgroundColor: '#D0D4E0' }}></span>
                <small>{app_main_wpml.or.toLowerCase()}</small>
                <span style={{ flex: 1, height: '1px', backgroundColor: '#D0D4E0' }}></span>
              </p>,
            ]
            : null}
          <Input
            className={cn('dedication__input')}
            shadowed
            errored={!!errors.billing_first_name}
            label={`${app_main_wpml.first_name}*`}
            name="billing_first_name"
            setRef={register({ required: true, maxLength: 1000 })}
            defaultValue={provided_user?.first_name ?? ''}
          />
          <ErrorMessage name="billing_first_name" />

          <Input
            className={cn('dedication__input')}
            shadowed
            errored={!!errors.billing_last_name}
            label={`${app_main_wpml.last_name}*`}
            name="billing_last_name"
            setRef={register({ required: true, maxLength: 1000 })}
            defaultValue={provided_user?.last_name ?? ''}
          />
          <ErrorMessage name="billing_last_name" />

          <Input
            className={cn('dedication__input')}
            shadowed
            errored={!!errors.billing_email}
            label={app_main_wpml.Email + '*'}
            name="billing_email"
            setRef={register({
              required: true,
              maxLength: 1000,
              pattern: /^\S+@\S+$/i,
            })}
            onChange={(e) => (e.target.value = e.target.value.trim())}
            defaultValue={provided_user?.email ?? ''}
          />
          <ErrorMessage name="billing_email" />

          <div style={{ position: 'relative' }}>
            <Input
              type={showPassword ? 'text' : 'password'}
              className={cn('dedication__input')}
              shadowed
              errored={!!errors.password}
              label={`${app_main_wpml.create_password}*`}
              name="password"
              setRef={register({
                required: true,
                maxLength: 1000,
                pattern: { value: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;\/`~]).{8,}$/ },
              })}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setPasswordTouched(true)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '15px',
                top: 'calc(50% - .375rem)',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '5px',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                color: '#5C6797',
              }}
              aria-label={showPassword ? app_main_wpml.hide_password : app_main_wpml.show_password}
            >
              <Icon icon={showPassword ? iconEyeOff : iconEye} width="20" height="20" />
            </button>
          </div>
          <ul style={{ listStyle: 'none', padding: '5px 0', margin: '5px 0', paddingLeft: '21px', marginTop: '0px', paddingTop: '0px', color: "#5C6797" }}>
            <li style={{ fontSize: '12px', marginBottom: '0px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {passwordValidation.minLength
                ? <CheckIcon />
                : (passwordTouched
                  ? <CrossIcon />
                  : <span style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#5C6797', display: 'inline-block' }}></span>
                )}{app_main_wpml.at_least_8_characters}
            </li>
            <li style={{ fontSize: '12px', marginBottom: '0px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {passwordValidation.hasUpperAndNumber
                ? <CheckIcon />
                : (passwordTouched
                  ? <CrossIcon />
                  : <span style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#5C6797', display: 'inline-block' }}></span>
                )}{app_main_wpml.at_least_one_uppercase_letter_and_number}
            </li>
            <li style={{ fontSize: '12px', marginBottom: '0px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {passwordValidation.hasSpecialChar
                ? <CheckIcon />
                : (passwordTouched
                  ? <CrossIcon />
                  : <span style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#5C6797', display: 'inline-block' }}></span>
                )}{app_main_wpml.at_least_one_special_character}
            </li>
          </ul>

          {props.prefixesAreVisible === '1' ? (
            <InputPhone
              className={cn('dedication__input')}
              label={`${app_main_wpml.phone}*`}
              shadowed
              errored={phoneErrored}
              name="billing_phone"
              dataGtmItem="profile-form"
              dataGtmDetail="phone"
              setRef={register({
                required: true,
                validate: isPhoneValid,
              })}
              onChange={(value) => setPhoneValue(value)}
              onBlur={() => {
                setPhoneTouched(true);
                trigger('billing_phone');
              }}
            />
          ) : (
            <Input
              className={cn('dedication__input')}
              shadowed
              errored={phoneErrored}
              label={`${app_main_wpml.phone}*`}
              name="billing_phone"
              setRef={register({
                required: true,
                validate: isPhoneValid,
              })}
              onChange={(e) => setPhoneValue(e.target.value)}
              onBlur={() => {
                setPhoneTouched(true);
                trigger('billing_phone');
              }}
            />
          )}
          {phoneErrored && (
            <span style={{ color: '#CC1D1D', fontSize: '12px', marginTop: '-0.3rem', marginBottom: '0.75rem', display: 'block', paddingLeft: '21px' }}>
              {errorMessages.billing_phone}
            </span>
          )}
          <p style={{ color: '#5C6797', fontSize: '12px', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '21px' }}>
            {app_main_wpml.will_use_your_phone_number_to_contact_you_about_your_order}
          </p>
        </div>
      </CheckoutCard></>
  );
};

Login.propTypes = {
  themeLink: PropTypes.string.isRequired,
  prefixesAreVisible: PropTypes.string,
};

export { Login };
